/* eslint-disable react-hooks/rules-of-hooks */
import { UI, WebViewerInstance } from '@pdftron/webviewer';
import {
  immutableJSONPatch,
  JSONPatchDocument,
  revertJSONPatch,
} from 'immutable-json-patch';
import React, { useEffect, useMemo, useState } from 'react';

import { externalAuthHeaderName } from 'api/apiReqSagaCreator/config';
import {
  handleExpiredTokens,
  httpClient,
} from 'api/apiReqSagaCreator/httpRequest';
import { DISABLE_SHARED_VIEWER } from 'utils/localFlags';

import { useWebviewerContext, useWebviewerInstance } from '.';

export function useWebViewer(
  element: React.RefObject<HTMLDivElement>,
  usePrivateInstance = false
): WebViewerInstance | undefined {
  const [isPrivate] = useState(
    usePrivateInstance || DISABLE_SHARED_VIEWER.value
  );

  if (isPrivate) {
    return useWebviewerInstance(element);
  }

  const viewerctx = useWebviewerContext();

  const instance = useMemo(
    () => (viewerctx !== null ? viewerctx.instance : undefined),
    [viewerctx]
  );

  useEffect(() => {
    if (viewerctx === null) return;

    viewerctx.deployViewer(element.current!);

    return viewerctx.returnViewer;
  }, [element, viewerctx]);

  return instance;
}

export async function checkDocumentUrl(
  url: string,
  externalToken: string
): Promise<string> {
  const result = await handleExpiredTokens(() =>
    httpClient.get(url, {
      method: 'GET',
      headers: {
        Range: 'bytes=1-1',
        ...(externalToken ? { [externalAuthHeaderName]: externalToken } : {}),
      },
    })
  );

  return result.request.responseURL;
}

/** @deprecated */
export function useWebviewerSettings(
  webViewerInstance: WebViewerInstance | undefined,
  enabled: boolean,
  features: Array<keyof typeof UI.Feature>,
  elements: string[]
) {
  const actualFeatures = useMemo(
    () =>
      features.map(
        (featureName) => webViewerInstance?.UI.Feature[featureName] ?? ''
      ),
    [features, webViewerInstance?.UI.Feature]
  );

  useEffect(() => {
    if (!webViewerInstance) return;

    if (enabled) {
      webViewerInstance.UI.enableFeatures(actualFeatures);
      webViewerInstance.UI.enableElements(elements);
    } else {
      webViewerInstance.UI.disableFeatures(actualFeatures);
      webViewerInstance.UI.disableElements(elements);
    }

    return () => {
      webViewerInstance.UI.enableFeatures(actualFeatures);
      webViewerInstance.UI.enableElements(elements);
    };
  }, [actualFeatures, elements, enabled, webViewerInstance]);
}

export function useWebviewerModularUI(
  webviewer?: WebViewerInstance,
  modifications?: JSONPatchDocument,
  functionMap?: { [key: string]: Function }
) {
  useEffect(() => {
    if (!(webviewer && modifications)) return;

    const initialUI: UI.ModularComponentsData =
      webviewer.UI.exportModularComponents();

    const updatedUI = immutableJSONPatch<UI.ModularComponentsData>(
      initialUI,
      modifications
    );

    webviewer.UI.importModularComponents(updatedUI, functionMap);

    return () => {
      const currrentUI: UI.ModularComponentsData =
        webviewer.UI.exportModularComponents();

      const reverseOps = revertJSONPatch(initialUI, modifications);

      const revertedUI = immutableJSONPatch<UI.ModularComponentsData>(
        currrentUI,
        reverseOps
      );

      webviewer.UI.importModularComponents(revertedUI);
    };
  }, [functionMap, modifications, webviewer]);
}
